/*
 * A quantity discount is a presentation switch rather than a re-render: the
 * price snippet writes out both the normal and the discounted view and CSS
 * picks between them. This sets the flag that decides which.
 *
 * The flag lands on two elements. On the price itself, which is what the price
 * snippet's own rules key off. And on the card, because the savings badge sits
 * on the product image, in a different branch of the tree, and CSS can only
 * reach across to it from a shared ancestor.
 */

const PRICE_SELECTOR = 'product-price[data-quantity-discount-min]';
const PRICE_ACTIVE_ATTRIBUTE = 'data-discount-active';
const SCOPE_ACTIVE_ATTRIBUTE = 'data-quantity-discount-active';

const QTY_INPUTS = ['input[ref="quantityInput"]', 'input[name="quantity"]'];
const QTY_INPUT_SELECTOR = QTY_INPUTS.join(', ');
const FORM_QTY_INPUT_SELECTOR = QTY_INPUTS.map((selector) => `product-form-component ${selector}`).join(', ');

/*
 * A card carries one price and one selector, but a product page carries two of
 * each: the pair in the buy buttons and the pair in the sticky bar. So a price
 * that sits inside one of those enclosures takes the selector from it, and
 * anything else asks the form first rather than taking whichever input the
 * section happens to hold first.
 */
const PRICE_ENCLOSURE_SELECTOR = 'product-card, .sticky-add-to-cart__bar';

/*
 * The price snippet renders the plated layout only for the card and the sticky
 * bar, so its presence is what tells this file which behaviour a price expects.
 * Everywhere else — search results above all, which are owned elsewhere and
 * list several products at once — keeps what the theme already did: the first
 * input the surrounding container holds, no flag written outwards, and no
 * wiring of prices that arrive after load. Reading the layout rather than
 * naming the surfaces means a price cannot end up with one file's markup and
 * the other's behaviour.
 */
const PLATED_SELECTOR = '.price__normal-container--plated, .price__discount-container--plated';

function findQuantityInput(el, container) {
    const enclosure = el.closest(PRICE_ENCLOSURE_SELECTOR);

    return (
        enclosure?.querySelector(QTY_INPUT_SELECTOR) ??
        container?.querySelector(FORM_QTY_INPUT_SELECTOR) ??
        (container || document).querySelector(QTY_INPUT_SELECTOR)
    );
}

function initEl(el, { initial = false } = {}) {
    if (el.dataset.quantityDiscountReady) return;

    const container = el.closest('.shopify-section, dialog, product-card');
    const plated = el.querySelector(PLATED_SELECTOR) != null;

    if (!plated && !initial) return;

    const qtyInput = plated
        ? findQuantityInput(el, container)
        : (container || document).querySelector(QTY_INPUT_SELECTOR);
    const min = parseInt(el.dataset.quantityDiscountMin, 10);

    if (!min || !qtyInput) {
        console.warn('Quantity discount: missing minimum or input', { min, qtyInput, el });
        return;
    }

    // Left off an unplated price, where the attribute would be the only thing
    // in the markup that differs from what that surface renders today.
    if (plated) el.dataset.quantityDiscountReady = 'true';

    // The card is the nearest thing that holds both the price and the badge on
    // the image. A product page has no card, so the section stands in for it,
    // save for the sticky bar, which would otherwise be a second writer on the
    // section's flag.
    const scope = plated ? el.closest(PRICE_ENCLOSURE_SELECTOR) || container : null;

    const apply = () => {
        const quantity = parseInt(qtyInput.value, 10) || 0;
        const active = quantity >= min;

        el.toggleAttribute(PRICE_ACTIVE_ATTRIBUTE, active);
        scope?.toggleAttribute(SCOPE_ACTIVE_ATTRIBUTE, active);
    };

    // Apply immediately on load
    apply();

    // Listen to all relevant events
    qtyInput.addEventListener('input', apply);
    qtyInput.addEventListener('change', apply);
    qtyInput.addEventListener('blur', apply);

    // Listen for the custom QuantitySelectorUpdateEvent
    const quantitySelector = qtyInput.closest('quantity-selector-component, cart-quantity-selector-component');
    if (quantitySelector) {
        quantitySelector.addEventListener('quantity-selector:update', apply);
    }

    // Use MutationObserver as a fallback to catch any value changes
    const observer = new MutationObserver(() => {
        apply();
    });

    observer.observe(qtyInput, {
        attributes: true,
        attributeFilter: ['value']
    });

    // Store observer to clean up later if needed
    qtyInput._quantityDiscountObserver = observer;
}

function initAll(root, options) {
    for (const el of root.querySelectorAll(PRICE_SELECTOR)) {
        initEl(el, options);
    }
}

function start() {
    initAll(document, { initial: true });

    /*
     * Cards keep arriving after load. Filtering, sorting, pagination and search
     * all replace grid markup in place, and those cards carry prices that were
     * never wired up, which left the discount view stuck on whatever the
     * server rendered.
     */
    new MutationObserver((mutations) => {
        for (const mutation of mutations) {
            for (const node of mutation.addedNodes) {
                if (node.nodeType !== Node.ELEMENT_NODE) continue;
                if (node.matches(PRICE_SELECTOR)) initEl(node);
                initAll(node);
            }
        }
    }).observe(document.body, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
} else {
    start();
}
